// src/components/RelatedProducts.tsx
//
// "You may also like" — sibling products from the same category, shown under
// the product page. Purely catalog-cache driven (CatalogContext); no extra
// network calls. The current product is excluded and the list is capped so the
// section stays a suggestion, not a second catalog page.

import React from "react";
import { CatalogObject } from "square";
import ProductList from "./ProductList";
import { useCatalog } from "../contexts/CatalogContext";
import { byMerchandisingOrder } from "../extensions/helpers";

const MAX_RELATED = 4;

type RelatedProductsProps = {
  /** The product currently being viewed (excluded from suggestions). */
  product?: CatalogObject;
  category?: CatalogObject;
  domain?: CatalogObject;
};

const RelatedProducts: React.FC<RelatedProductsProps> = ({
  product,
  category,
  domain,
}) => {
  const { getItemsByCategory } = useCatalog();

  // Order by the owner's arrangement BEFORE slicing, so "you may also like"
  // shows the products they put first in this category rather than whichever
  // ones the catalog happened to return first. (ProductList no longer sorts.)
  const related = byMerchandisingOrder(
    getItemsByCategory(category?.id ?? "")?.filter(
      (item) => item.id !== product?.id,
    ) ?? [],
    category?.id,
  ).slice(0, MAX_RELATED);

  if (!related || related.length === 0) return null;

  return (
    <section className="w-full flex flex-col gap-2 mt-8">
      <h2 className="text-xl font-semibold">You may also like</h2>
      <ProductList domain={domain} category={category} items={related} />
    </section>
  );
};

export default RelatedProducts;
